import { useState } from "react";
import axios from "axios";
import { Loader2, Mail, MessageSquare } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { btn, card } from "@/components/org/intake/intakeMeta";

export const IdentityStep = ({ model, platformSmsOtpAvailable, onSaved }) => {
  const { API } = useAuth();
  const [methods, setMethods] = useState(model.identity_verification?.methods || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const toggle = (m) => setMethods((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));

  const save = async () => {
    setError("");
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/org/intake/models/${model.model_id}/identity-verification`, { methods }, { withCredentials: true });
      onSaved(data.model);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Couldn't save.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={card} data-testid="wizard-step-identity">
      <p className="text-sm font-bold text-slate-950 dark:text-white">Identity Verification</p>
      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Customers must verify a one-time code before their submission is accepted. Choose which method(s) are offered.</p>

      <div className="mt-4 space-y-2">
        <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 dark:border-white/10" data-testid="identity-method-email">
          <input type="checkbox" checked={methods.includes("email")} onChange={() => toggle("email")} />
          <Mail size={16} className="text-slate-400" />
          <div>
            <p className="text-sm font-bold text-slate-800 dark:text-slate-100">Email OTP</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Sends a verification code via Nova's email infrastructure.</p>
          </div>
        </label>
        <label className="flex items-center gap-3 rounded-xl border border-slate-200 p-4 dark:border-white/10" data-testid="identity-method-sms">
          <input type="checkbox" checked={methods.includes("sms")} onChange={() => toggle("sms")} />
          <MessageSquare size={16} className="text-slate-400" />
          <div>
            <p className="text-sm font-bold text-slate-800 dark:text-slate-100">Phone / SMS OTP</p>
            <p className="text-xs text-slate-500 dark:text-slate-400">Sends a verification code via Twilio Verify (platform-managed). Falls back to Email OTP automatically if SMS delivery is unavailable.</p>
          </div>
        </label>
      </div>

      {methods.includes("sms") && !platformSmsOtpAvailable && (
        <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs font-semibold text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-400" data-testid="identity-sms-unavailable-notice">
          SMS OTP requires Twilio Verify to be configured platform-wide, which is pending setup. Until then, customers using this form will see Email OTP instead if it's also enabled above.
        </p>
      )}

      {error && <p className="mt-3 text-xs font-semibold text-red-600 dark:text-red-400" data-testid="wizard-identity-error">{error}</p>}
      <button type="button" onClick={save} disabled={saving} data-testid="wizard-identity-save-btn" className={`${btn} mt-5`}>
        {saving && <Loader2 size={14} className="animate-spin" />} Save & Continue
      </button>
    </div>
  );
};
